/* ------------------------------------------------------------------------
Copyright (c) 2016, VoiceVibes, Inc

VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
All contents of this file are proprietary and confidential. No part of
any of the contents of this file may be disclosed in any manner to
a third party without the prior written consent of VoiceVibes, Inc.
Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    class="col-lg-10"
  >
    <form @submit.prevent="save">
      <h2>
        Customize Scorecard
      </h2>
      Scorecards allow coaches and speakers to provide ratings on each other's recordings. By default,
      the scorecard for this assignment's recordings will consist of four specific ratings, shown below.
      You are able to customize some aspects of these four ratings, as well as add your own custom ratings,
      via the form below.
      <br><br>
      <div class="row">
        <accordion
          :ref="accordionRef"
          :one-at-atime="true"
          class="col-md-8"
          type="info"
        >
          <panel
            v-for="(rating) in ratings"
            :key="rating.id"
            :ref="rating.id"
            :header="'+ ' + rating.label"
            :class="rating.hidden ? 'translucentPanel' : ''"
          >
            <div
              v-if="editing !== rating.id"
              class="ratingPreview"
            >
              <div class="ratingPreviewInner">
                <label
                  :id="'radio' + rating.id"
                  style="font-size: 14px;font-weight: bold; display: block;"
                >
                  {{ rating.label }}
                </label>
                <div class="scoreDescription">
                  {{ rating.description }}
                </div>
                <span
                  v-for="i in [1,2,3,4,5,6,7,8,9,10]"
                  :key="i"
                  class="form-group"
                >
                  <label
                    :style="(i>1) ? 'margin-left: 1.2em;' : ''"
                    :for="rating.id + i + 'radio'"
                  >
                    {{ i }}
                    <input
                      :aria-labelledby="'radio' + rating.id"
                      style="display: block;"
                      type="radio"
                      :value="i"
                      :name="rating.id"
                      :for="rating.id + i + 'radio'"
                    >
                  </label>
                </span><br>
                <span
                  aria-hidden="true"
                  class="descriptor"
                >
                  Poor
                </span>
                <span
                  style="padding-left: 200px;"
                  aria-hidden="true"
                  class="descriptor"
                >
                  Excellent
                </span>
              </div>
              <h3>
                <a
                  href="#"
                  @click.prevent="editing = rating.id"
                >
                  Click to edit
                </a>
              </h3>
            </div>
            <div v-if="editing === rating.id">
              <label
                v-if="!rating.preset"
                class="control-label blockLabel"
              >
                <div>Rating Name</div>
              </label>
              <input
                v-if="!rating.preset"
                v-model.trim="rating.label"
                type="text"
                style="width: 330px;"
                maxlength="100"
              ><br><br>
              <label
                class="control-label blockLabel"
              >
                <div class="descriptionLabel">
                  Description
                </div>
              </label>
              <textarea
                :id="'rating' + rating.id"
                v-model.trim="rating.description"
                type="text"
                class="ratingsDescription"
                style="height: 120px; width: 330px;"
                name="name"
                maxlength="1000"
              />
              <br>
              <label
                v-if="rating.id !== 'overall' && (!assignment.isTemplate || rating.preset)"
                :for="'hideRating' + rating.id"
                style="padding-left: 5px;"
              >
                <input
                  v-if="rating.id !== 'overall' && (!assignment.isTemplate || rating.preset)"
                  :id="'hideRating' + rating.id"
                  v-model="rating.hidden"
                  type="checkbox"
                  value="1"
                >
                Hide this Rating / Do Not Use
              </label>
              <div
                class="moveRating"
              >
                <label
                  v-if="!rating.preset && ratings.length > 5"
                  :for="'movement' + rating.id"
                  style="padding-left: 5px;"
                >
                  <span>Move this Rating Up or Down:</span>
                  <svg
                    v-if="rating.order > 5"
                    class="icon-arrow-up"
                    style="width: 20px; height: 20px;"
                    @click.prevent="moveRating('up', rating)"
                  >
                    <use xlink:href="/static/svg-symbols.svg#icon-arrow-up" />
                  </svg>
                  <svg
                    v-if="rating.order !== maxOrder"
                    class="icon-arrow-down"
                    style="width: 20px; height: 20px;"
                    @click.prevent="moveRating('down', rating)"
                  >
                    <use xlink:href="/static/svg-symbols.svg#icon-arrow-down" />
                  </svg>
                </label>

                <span
                  v-if="assignment.isTemplate && !rating.preset"
                  tabindex="0"
                  aria-label="delete this rating"
                  role="button"
                  style="float: right; margin-top: 17px; margin-left: 20px;"
                  @click.prevent="deleteRating(rating)"
                  @keypress="deleteRating(rating, $event)"
                >
                  <svg class="svgicon icon-trash">
                    <use xlink:href="/static/svg-symbols.svg#icon-trash" />
                  </svg>
                </span>
                <a
                  href="#"
                  style="float: right; margin-top: 25px;"
                  @click.prevent="editing = null;"
                >
                  View Preview
                </a>
              </div>
            </div>
          </panel>
        </accordion>
      </div>
      <div
        v-if="ratingLabelChanged"
        id="saveWarning"
        class="text-danger"
        role="alert"
        style="margin-left: 8px;"
      >
        Warning: You are about to change the Rating Name for one of your custom fields.
        If you have pre-existing scores that are already present for this rating,
        changing the name might make those scores have a new, inaccurate label.
        Perhaps you want to {{ assignment.isTemplate ? 'delete' : 'hide/archive' }}
        this Rating Name and add a new one instead?
        <br><br>
      </div>
      <button
        class="btn btn-primary clear-btn"
        @click.self.prevent="addRating"
      >
        Add New Rating
      </button>
      <br><br><br>

      <h4>
        Scorecard Settings
      </h4>
      <label
        for="coachOnlyScorecard"
        style="padding-left: 5px;"
      >
        <input
          id="coachOnlyScorecard"
          v-model="coachOnlyScorecard"
          type="checkbox"
          value="1"
        >
        Hide Scorecard from Peers / Coach-Only Scorecard
      </label>
      <br><br><br>

      <button
        type="button"
        class="btn clear-btn"
        @click="$emit('rewindAssignmentEditor')"
      >
        Cancel / Previous
      </button>
      <loading-button
        style="float:right"
        :loading="saving"
      >
        Save and Continue
      </loading-button>
    </form>
  </div>
</template>

<style lang="less" scoped>
  .svgicon {
    display: inline-block;
    height: 40px;
    width: 40px;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
  }
  .ratingPreviewInner {
    width: 350px;
  }
  .ratingPreview:hover {
    position: relative;
    .ratingPreviewInner {
      opacity: 0.4;
    }
    z-index: 10;
    h3 {
      display: block;
    }
  }
  .ratingPreview {
    h3 {
      display: none;
      position: absolute;
      top: 5px;
      left: 10%;
      background-color: white;
      padding: 15px;
    }
  }
  .scoreDescription {
    max-height: 200px;
    overflow-y: auto;
    margin-bottom: 1em;
  }
  .ratingsDescription {
    font-size: 14px;
    font-weight: normal !important;
    width: 300px;
    height: 200px;
  }
  .blockLabel {
    text-transform: uppercase;
    display: block;
    font-size: 12px !important;
  }
  .moveRating {
    i {
      cursor: pointer;
    }
    label {
      margin-top: 12px;
    }
    span {
      font-size: 12pt;
      position: relative;
      bottom: 5px;
    }
    i {
      font-size: 30pt;
      margin-left: 5px;
    }
  }
</style>

<script>

import Accordion from 'vue-strap/src/Accordion';
import Panel from 'vue-strap/src/Panel';

export default {
  components: { Accordion, Panel },
  props: [ 'assignment' ],
  data() {
    return {
      accordionRef: 1,
      errors: [],
      saving: false,
      ratings: [],
      originalRatings: [],
      words: null,
      originalWords: null,
      wordsType: 'global',
      editing: null,
      coachOnlyScorecard: null,
    };
  },
  computed: {
    ratingLabelChanged: {
      cache: false,
      get() {
        let ratingLabelChanged = false;
        this.ratings.forEach((rating) => {
          if (!rating.preset) {
            // We know they changed something if the ID is in there
            // and the labels aren't the same
            const preexisting = this._.find(this.originalRatings, {id: rating.id});
            if (preexisting && preexisting.label && preexisting.label !== rating.label) {
              ratingLabelChanged = true;
            }
          }
        });
        return ratingLabelChanged;
      }
    },
    coreScores() {
      return this.$store.getters.appConfig.coreScores;
    },
    org() {
      return this.$store.getters.org;
    },
    activeGroups: {
      cache: false,
      get() {
        return this.$store.getters.activeGroups;
      }
    },
    maxOrder: {
      cache: false,
      get() {
        const max = this._.maxBy(this.ratings, (o) => o.order);
        return max.order;
      }
    },
  },
  mounted() {
    this.loadRatings();
  },
  methods: {
    addRating() {
      const id = 'newrating000-' + Math.random();
      const newRating = {
        id,
        label: 'New Rating',
        description: '',
        order: this.maxOrder + 1,
      };
      this.editing = id;
      this.ratings.push(newRating);
      this.$nextTick(() => {
        document.querySelector('div.panel-info:last-child div.accordion-toggle').click();
      });
    },
    renumberRatings() {
      let i = 1;
      this.ratings.forEach((r) => r.order = i++);
    },
    deleteRating(rating) {
      if (!window.confirm('Delete this rating?')) return;
      this.ratings = this._.remove(this.ratings, (r) => r.id !== rating.id);
      this.renumberRatings();
      this.accordionRef++;
    },
    moveRating(direction, rating) {
      const origin = rating.order;
      const target = (direction === 'up') ?
        rating.order - 1 : rating.order + 1;
      if (target < 5 || target > this.maxOrder) return;

      this.ratings.forEach((r) => {
        if (r.order === origin) {
          r.order = target;
        } else if (r.order === target) {
          r.order = origin;
        }
      });
      this.ratings = this._.sortBy(this.ratings, 'order');
      this.accordionRef++;
    },
    loadRatings() {
      if (this.assignment.scorecard && this.assignment.scorecard.scorecard &&
        this.assignment.scorecard.scorecard.questions) {
        this.ratings = this._.cloneDeep(this.assignment.scorecard.scorecard.questions);
        this.originalRatings = this.assignment.scorecard.scorecard.questions;
        this.coachOnlyScorecard = this.assignment.scorecard.scorecard.coachOnlyScorecard;
        return true;
      }
      this._.forEach(this.coreScores, (rating) => {
        const _r = this._.cloneDeep(rating);
        _r.preset = true;
        this.ratings.push(_r);
      });
    },
    save() {
      this.saving = true;

      // Do not mutate state
      const assignment = this._.cloneDeep(this.assignment);
      let questions = this._.cloneDeep(this.ratings);

      // Hidden delete functionality
      questions = this._.remove(questions, (r) => r.label.toLowerCase() !== 'please delete');

      let prompt = null;
      let allIds = [];
      questions.forEach((question) => {
        if (question.label == 'New Rating') {
          prompt = 'Please assign a name to the new field currently named "New Rating"';
        }

        // Set the id on all newly added fields
        if (question.id.indexOf('newrating000') !== -1) {
          question.id = 'custom' + question.label.replace(/[^a-zA-Z0-9\.\-]/g, '').toLowerCase();
        }
        if (allIds.indexOf(question.id) !== -1) {
          prompt = `You already have a rating that uses the name "${question.label}" ` +
            '(or something similar) and you cannot use this name again. ' +
            'Please change the name of one of your ' +
            'new ratings to be different.';
        }
        allIds.push(question.id);
      });
      if (prompt) {
        this.saving = false;
        return window.alert(prompt);
      }

      const coachOnlyScorecard = this.coachOnlyScorecard ? true : false;
      const scorecard = {
        assignmentId: assignment.assignmentId,
        scorecard: {
          questions,
          coachOnlyScorecard,
        },
        scorecardType: 'recording',
      };
      if (assignment.scorecard && assignment.scorecard.id) {
        scorecard.id = assignment.scorecard.id;
      }

      this.$store.dispatch(
        'saveAssignmentScorecard',
        {assignment, scorecard}
      )
        .then(() => {
          // Advance to the next form
          this.$emit('advanceAssignmentEditor');
        })
        .catch(() => {
          this.saving = false;
        });
    },
  }
};
</script>
